// tRPC React client (Phase 3.5 — активация).
//
// AppRouter type-инференс приходит из workspace-зависимости @studyhub/server
// через path alias `@server/trpc-router` → apps/server/dist/src/shared/trpc/router.d.ts
// (см. apps/web/tsconfig.json). Использовать ТОЛЬКО `import type` — никаких
// runtime-импортов из apps/server.
//
// Транспорт:
//   • httpBatchLink на /api/trpc (батчинг GET для queries, POST для mutations)
//   • Кастомный fetch — добавляет Authorization: Bearer <token> и
//     credentials: "include" (refresh-token в HttpOnly cookie на web /
//     SecureStorage на Capacitor — см. lib/auth-storage.ts).
//   • 401 interceptor — fetch wrapper открывает AuthExpiredModal
//     (`useAuthExpiredStore.show()`), кроме /auth/* paths (там 401 = invalid creds).
//   • Без superjson (server tRPC `initTRPC.create({})` без transformer; дата —
//     ISO-string на уровне Zod-схем).
//
// SSE / streaming endpoints (chat stream, notes generation-progress,
// notes ai-blocks SSE) — НЕ через tRPC; раздельные raw fetch в feature-хуках.

import type { AssembledAppRouter } from "@server/trpc-router";
import { httpBatchLink } from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import { notifySessionExpiredFrom401 } from "./auth-expired-store";
import { getAccessToken } from "./auth-storage";
import { DEFAULT_FETCH_TIMEOUT_MS, withFetchTimeout } from "./fetch-timeout";

/**
 * Типизированный tRPC React hooks namespace.
 * Использование: `api.notes.list.useQuery()`, `api.notes.create.useMutation()`.
 */
export const api = createTRPCReact<AssembledAppRouter>();

// API base: абсолютный VITE_API_URL (Capacitor — origin https://localhost не имеет бэкенда)
// либо same-origin "" (web, Caddy/Vite proxy). См. auth-client.ts API_BASE.
const TRPC_URL = `${import.meta.env.VITE_API_URL ?? ""}/api/trpc`;

/**
 * Paths-исключения для 401-интерсептора: на них 401 = invalid credentials
 * (показывается в форме логина), а не "сессия истекла" → не открываем модалку.
 */
const AUTH_EXCLUDE_PATTERNS = [
	"/auth.login",
	"/auth.refresh",
	"/auth.consent",
	"/auth.logout",
	"/auth.logoutAll",
];

function shouldTriggerAuthExpired(url: string): boolean {
	return !AUTH_EXCLUDE_PATTERNS.some((pattern) => url.includes(pattern));
}

/**
 * Кастомный fetch для tRPC link.
 *
 * Поведение:
 *   1. Добавляет credentials: "include" (refresh cookie).
 *   2. На 401 → открывает AuthExpiredModal (кроме auth.* endpoints).
 *   3. Возвращает оригинальный Response — tRPC сам сериализует ошибку.
 */
async function fetchWithAuthInterceptor(
	url: RequestInfo | URL,
	options?: RequestInit,
): Promise<Response> {
	// Потолок ожидания: без него VPN/прокси оставляли isLoading=true навсегда
	// (дашборд, progress, recommendations — полный скелетон после reopen).
	const res = await fetch(
		url,
		withFetchTimeout(
			{
				...options,
				credentials: "include",
			},
			DEFAULT_FETCH_TIMEOUT_MS,
		),
	);
	if (res.status === 401) {
		const urlString = typeof url === "string" ? url : url.toString();
		if (shouldTriggerAuthExpired(urlString)) {
			try {
				notifySessionExpiredFrom401();
			} catch {
				// Store не инициализирован (early bootstrap) — игнор.
			}
		}
	}
	return res;
}

/**
 * Factory tRPC vanilla client.
 * Использовать в `useState(() => createTrpcClient())` внутри AppProviders —
 * один client на жизненный цикл root.
 */
export function createTrpcClient() {
	return api.createClient({
		links: [
			httpBatchLink({
				url: TRPC_URL,
				fetch: fetchWithAuthInterceptor,
				/** Headers пересчитываются на каждый запрос — токен может ротироваться. */
				headers() {
					const token = getAccessToken();
					return token ? { authorization: `Bearer ${token}` } : {};
				},
			}),
		],
	});
}
